<template>
  <div class="scoreboard">
    <ScoreBar />
    <div class="content">
      <div class="board-head">
        <GameTransfer variant="inline" :show-import="false" />
      </div>
      <div v-if="roundHistoryTable.rows.length === 0" class="empty">Nog geen rondes gespeeld</div>
      <div v-else ref="tableCard" class="table-card">
        <table class="history-table">
          <thead>
            <tr>
              <th class="round-col">#</th>
              <th class="detail-col">Contract</th>
              <th class="detail-col">Resultaat</th>
              <th v-for="player in roundHistoryTable.players" :key="player.id">{{ player.name }}</th>
              <th class="edit-col"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in roundHistoryTable.rows" :key="row.roundNumber">
              <td class="round-col">{{ row.roundNumber }}</td>
              <td class="detail-col">
                <template v-if="row.contract">
                  <span class="contract-label">{{ row.contract.label }}</span>
                  <span v-if="row.contract.suitSymbol" class="contract-suit" :class="{ red: row.contract.suitRed }">{{ row.contract.suitSymbol }}</span>
                  <span v-if="row.contract.tricks" class="contract-tricks">{{ row.contract.tricks }}t</span>
                  <span class="contract-players">{{ contractPlayerNames(row.contract.playerIds) }}</span>
                </template>
              </td>
              <td class="detail-col">
                <span v-if="row.result" class="result-text" :class="resultClass(row)">{{ resultText(row) }}</span>
              </td>
              <td v-for="pp in row.playerPoints" :key="pp.id">
                <span class="points-badge" :class="{ positive: pp.points > 0, negative: pp.points < 0 }">
                  {{ pp.points > 0 ? '+' : '' }}{{ pp.points }}
                </span>
              </td>
              <td class="edit-col">
                <button
                  v-if="row.roundNumber === lastRoundNumber"
                  class="edit-btn"
                  aria-label="Wijzig laatste ronde"
                  @click="startEditLastRound"
                >✎</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <AppFooter
      :continue-label="`Ronde #${roundNumber} starten`"
      show-end-game
      @continue="startRound"
      @end-game="requestEndGame"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useGameStore } from '../../stores/game'
import ScoreBar from '../shared/ScoreBar.vue'
import AppFooter from '../shared/AppFooter.vue'
import GameTransfer from '../shared/GameTransfer.vue'

export default {
  name: 'GameScoreboard',
  components: { ScoreBar, AppFooter, GameTransfer },
  computed: {
    ...mapState(useGameStore, ['roundNumber', 'roundHistoryTable']),
    lastRoundNumber() {
      const rows = this.roundHistoryTable.rows
      return rows.length ? rows[rows.length - 1].roundNumber : null
    }
  },
  mounted() {
    this.scrollToBottom()
  },
  updated() {
    this.scrollToBottom()
  },
  methods: {
    ...mapActions(useGameStore, ['startRound', 'requestEndGame', 'startEditLastRound']),
    resultText(row) {
      const r = row.result
      const c = row.contract
      if (!r || !c) return ''

      // Tricks-based contracts
      if (r.tricksTaken !== null && r.tricksTaken !== undefined) {
        const bid = c.tricks
        if (bid) {
          return r.tricksTaken >= bid
            ? `${r.tricksTaken}/${bid} slagen`
            : `${r.tricksTaken}/${bid} slagen`
        }
        return `${r.tricksTaken} slagen`
      }

      // Single result (troel, solo-slim)
      if (r.singleResult !== null && r.singleResult !== undefined) {
        return r.singleResult ? 'Geslaagd' : 'Mislukt'
      }

      // Per-player results (miseries)
      if (r.playerResults && c.playerIds) {
        const results = c.playerIds.map(id => {
          const name = this.roundHistoryTable.players.find(p => p.id === id)?.name || ''
          const ok = r.playerResults[id]
          return `${name}: ${ok ? '✓' : '✗'}`
        })
        return results.join(', ')
      }

      return ''
    },

    resultClass(row) {
      const r = row.result
      const c = row.contract
      if (!r || !c) return ''

      if (r.tricksTaken !== null && r.tricksTaken !== undefined && c.tricks) {
        return r.tricksTaken >= c.tricks ? 'success' : 'failure'
      }

      if (r.singleResult !== null && r.singleResult !== undefined) {
        return r.singleResult ? 'success' : 'failure'
      }

      // Per-player: mixed results, no single class
      return ''
    },

    contractPlayerNames(playerIds) {
      if (!playerIds || !playerIds.length) return ''
      return playerIds
        .map(id => {
          const p = this.roundHistoryTable.players.find(pl => pl.id === id)
          return p ? p.name : ''
        })
        .join(', ')
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.tableCard
        if (el) el.scrollTop = el.scrollHeight
      })
    }
  }
}
</script>

<style scoped>
.scoreboard {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.content {
  flex: 1;
  min-height: 0;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
}

.board-head {
  display: flex;
  justify-content: flex-end;
  padding-bottom: 0.5rem;
}

.empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  color: #888;
}

.table-card {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.history-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.history-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #2c3e50;
  color: #fff;
  padding: 0.75rem 0.6rem;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-table thead .round-col {
  color: rgba(255, 255, 255, 0.4);
}

.history-table tbody tr:nth-child(even) td {
  background: #f6faf6;
}

.history-table tbody tr:not(:last-child) td {
  border-bottom: 1px solid #eef3ee;
}

.history-table td {
  padding: 0.6rem 0.5rem;
  text-align: center;
}

.round-col {
  width: 2.5rem;
  font-size: 0.78rem;
  color: #bbb;
  font-weight: 500;
}

.detail-col {
  display: table-cell;
  width: 8rem;
  text-align: left;
  font-size: 0.82rem;
}

.contract-label { font-weight: 600; color: #2c3e50; }
.contract-suit { color: #2c3e50; }
.contract-suit.red { color: #c0392b; }
.contract-tricks { font-size: 0.8rem; color: #555; }
.contract-players { display: block; font-size: 0.7rem; color: #888; }

.result-text {
  font-size: 0.82rem;
  font-weight: 600;
  color: #555;
}
.result-text.success { color: #27ae60; }
.result-text.failure { color: #c0392b; }

.points-badge {
  display: inline-block;
  min-width: 2.75rem;
  padding: 0.25em 0.5em;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 700;
  background: #f0f0f0;
  color: #999;
}
.points-badge.positive { background: #e6f6ed; color: #27ae60; }
.points-badge.negative { background: #fdecea; color: #c0392b; }

.edit-col {
  width: 3.5rem;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}

.edit-btn {
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  font-size: 1.1rem;
  font-family: inherit;
  border: 2px solid #2c3e50;
  border-radius: 8px;
  background: #fff;
  color: #2c3e50;
  cursor: pointer;
  touch-action: manipulation;
}
.edit-btn:active { opacity: 0.75; }

/* Hide detail columns on very small screens */
@media (max-width: 480px) {
  .detail-col { display: none; }
}
</style>
